import {
  Component,
  ChangeDetectionStrategy,
  viewChild,
  effect,
  inject,
  DestroyRef,
  input,
  computed,
  output,
  signal,
  untracked
} from '@angular/core';
import { HashMap, ZefReactiveComponent } from '@zerops/zef/core';
import { NgTerminalComponent } from 'ng-terminal';
import { Terminal } from '@xterm/xterm';
import {
  Subject,
  timer,
  merge,
  combineLatest,
  switchMap,
  catchError,
  retry,
  filter,
  distinctUntilChanged,
  withLatestFrom,
  tap,
  defer,
  takeUntil,
  map,
  take,
  pairwise,
  of,
  EMPTY
} from 'rxjs';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { WebSocketSubject } from 'rxjs/webSocket';
import { TerminalApi } from './terminal.api';
import { Container } from '@zerops/zerops/core/container-base';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { ClipboardAddon } from '@xterm/addon-clipboard';
import { ContainerStatuses } from '@zerops/models/container';

enum WebsocketMessageTypes {
  Pong = 1,
  Io = 2,
  Resize = 3
}

const MAX_RETRY_COUNT = 5;

// Connection state machine
type ConnectionState =
  | { status: 'initializing' }
  | { status: 'container_inactive' }
  | { status: 'connecting'; isReconnect: boolean }
  | { status: 'connected' }
  | { status: 'disconnected' }
  | { status: 'retrying'; attempt: number }
  | { status: 'failed' }
  | { status: 'unauthorized' };

@Component({
  selector: 'z-terminal',
  templateUrl: './terminal.feature.html',
  styleUrl: './terminal.feature.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TerminalFeature extends ZefReactiveComponent {
  // # Deps
  #destroyRef = inject(DestroyRef);
  #api = inject(TerminalApi);

  // # Inputs
  serviceId = input<string>();
  containerId = input<string>();
  showFilters = input<boolean>(true);
  scrollHeight = input<string>('500px');
  containers = input<Container[]>();

  // # Outputs
  filterChange = output<any>();
  terminalOutput = output<string>();

  // # Data
  // -- sync
  terminalInstance: Terminal;
  terminalRef = viewChild(NgTerminalComponent);
  containerMap = computed<HashMap<Container>>(() => {
    if (this.containers()?.length) {
      return this.containers().reduce((obj: HashMap<Container>, itm) => {
        obj[itm.id] = itm;
        return obj;
      }, {});
    }
    return {};
  });

  // -- async
  #serviceId$ = toObservable(this.serviceId);
  #containerId$ = toObservable(this.containerId);
  #containers$ = toObservable(this.containers);
  #wsConnection$: WebSocketSubject<any>;
  #manualReconnectTrigger$ = new Subject<void>();

  // Simple mode: only serviceId provided, connect immediately without container lifecycle checks
  // Managed mode: containers provided, wait for container to be active before connecting
  #container$ = combineLatest({
    id: this.#containerId$,
    containers: this.#containers$
  }).pipe(
    filter(({ id, containers }) => !!id && !!containers?.length),
    map(({ id, containers }) => containers.find(c => c.id === id)),
    filter((c) => !!c)
  );

  #containerStatus$ = this.#container$.pipe(
    map((container) => container.status),
    distinctUntilChanged()
  );

  // In simple mode (no containers), always "active" so connection proceeds immediately
  #containerActive$ = this.#containers$.pipe(
    switchMap((containers) =>
      containers?.length
        ? this.#containerStatus$.pipe(map((status) => !new Set([ContainerStatuses.Deleting, ContainerStatuses.Stopped, ContainerStatuses.Moving]).has(status)))
        : of(true)
    ),
    distinctUntilChanged()
  );

  // -- state
  connectionState = signal<ConnectionState>({ status: 'initializing' });

  // Computed for template convenience
  isConnected = computed(() => this.connectionState().status === 'connected');
  isConnecting = computed(() =>
    this.connectionState().status === 'initializing' ||
    this.connectionState().status === 'connecting' ||
    this.connectionState().status === 'retrying'
  );
  isContainerInactive = computed(() => this.connectionState().status === 'container_inactive');
  isUnauthorized = computed(() => this.connectionState().status === 'unauthorized');
  canReconnect = computed(() =>
    this.connectionState().status === 'disconnected' ||
    this.connectionState().status === 'failed' ||
    this.connectionState().status === 'unauthorized'
  );

  constructor() {
    super();

    // Initialize terminal when ref is available
    effect(() => {
      if (this.terminalInstance || !this.terminalRef()) {
        return;
      }
      this.terminalInstance = this.terminalRef().underlying;
      untracked(() => this.#init());
    });

    // Send resize on connect
    effect(() => {
      if (this.isConnected()) {
        setTimeout(() => {
          this.send(
            JSON.stringify({
              rows: this.terminalInstance.rows,
              cols: this.terminalInstance.cols
            }),
            WebsocketMessageTypes.Resize
          );
        });
      }
    });
  }

  focus() {
    this.terminalRef().underlying?.focus();
  }

  send(message: string, type = WebsocketMessageTypes.Io) {
    if (this.#wsConnection$ && !this.#wsConnection$.closed) {
      this.#wsConnection$.next({ type, message });
    } else {
      console.warn('WebSocket is not connected. Message not sent:', message);
    }
  }

  reconnect() {
    this.#manualReconnectTrigger$.next();
  }

  // # State transition - sets state AND writes terminal message
  #setConnectionState(next: ConnectionState) {
    const prev = this.connectionState();
    const statusChanged = prev.status !== next.status;

    this.connectionState.set(next);

    // Write terminal messages on transitions (only if terminal is ready)
    if (!this.terminalInstance) return;

    // Container became inactive (only show if we were connected before)
    if (statusChanged && next.status === 'container_inactive' && prev.status === 'connected') {
      this.#writeTerminalMessage('yellow',
        'Container stopped - Terminal disconnected',
        'Waiting for container to become active...'
      );
    }

    // Reconnected successfully (only on reconnect, not initial connect)
    if (statusChanged &&
        next.status === 'connected' &&
        (
          (prev.status === 'connecting' && prev.isReconnect) ||
          prev.status === 'retrying'
        )) {
      this.#writeTerminalMessage('green', 'Reconnected');
    }

    // Retrying connection (always show, even when attempt number changes)
    if (next.status === 'retrying') {
      this.#writeTerminalLine('yellow',
        `Retrying connection (attempt ${next.attempt}/${MAX_RETRY_COUNT})...`
      );
    }

    // Access denied - no retries, only an explicit user attempt can change this
    if (statusChanged && next.status === 'unauthorized') {
      this.#writeTerminalMessage('red',
        'Access denied - You are not allowed to use the terminal',
        'Your role in this project does not grant terminal access'
      );
    }

    // Failed after all retries
    if (statusChanged && next.status === 'failed') {
      this.#writeTerminalMessage('red',
        'Connection failed - All retry attempts exhausted',
        'Click "Reconnect" to try again'
      );
    }

    // Disconnected (but not due to container stopping)
    if (statusChanged && next.status === 'disconnected' && prev.status === 'connected') {
      this.#writeTerminalMessage('yellow', 'Disconnected');
    }

    // Reconnecting after failure (user clicked Reconnect button)
    if (statusChanged &&
        next.status === 'connecting' &&
        next.isReconnect &&
        prev.status === 'failed') {
      this.#writeTerminalLine('yellow', 'Reconnecting...');
    }
  }

  #writeTerminalMessage(color: 'green' | 'yellow' | 'red', ...lines: string[]) {
    const colorCode = { green: '32', yellow: '33', red: '31' }[color];
    this.terminalInstance.writeln('');
    this.terminalInstance.writeln(`\x1b[${colorCode}m═══════════════════════════════════════════════════════\x1b[0m`);
    lines.forEach(line => {
      this.terminalInstance.writeln(`\x1b[${colorCode}m  ${line}\x1b[0m`);
    });
    this.terminalInstance.writeln(`\x1b[${colorCode}m═══════════════════════════════════════════════════════\x1b[0m`);
  }

  #writeTerminalLine(color: 'green' | 'yellow' | 'red', text: string) {
    const colorCode = { green: '32', yellow: '33', red: '31' }[color];
    // Start on a new line to avoid mixing with user input
    this.terminalInstance.writeln('');
    this.terminalInstance.writeln(`\x1b[${colorCode}m${text}\x1b[0m`);
  }

  // # Stream helpers
  #getInputChanges$() {
    return combineLatest({
      serviceId: this.#serviceId$.pipe(distinctUntilChanged()),
      containerId: this.#containerId$.pipe(distinctUntilChanged())
    }).pipe(
      filter(({ serviceId }) => !!serviceId)
    );
  }

  #getContainerBecomesActive$() {
    // Track false→true transitions (container becoming active after being inactive)
    // In simple mode (no containers), this never emits since container is always "active"
    return this.#containers$.pipe(
      switchMap((containers) =>
        containers?.length
          ? this.#containerActive$.pipe(
              pairwise(),
              filter(([ prev, curr ]) => !prev && curr),
              map(([ _, curr ]) => curr)
            )
          : EMPTY
      )
    );
  }

  #getContainerBecomesInactive$() {
    // In simple mode (no containers), this never emits
    return this.#containers$.pipe(
      switchMap((containers) =>
        containers?.length
          ? this.#containerActive$.pipe(filter((isActive) => !isActive))
          : EMPTY
      )
    );
  }

  // # Connection stream - clean, state-driven
  #createWebSocketConnection$(serviceId: string, containerId: string) {
    let hasReceivedMessage = false;
    let wasGracefulClose = false;

    return this.#api.auth$(serviceId, containerId).pipe(
      switchMap(({ accessToken, host }) =>
        defer(() => {
          // Reset flags for each new connection attempt (including retries)
          hasReceivedMessage = false;
          wasGracefulClose = false;
          this.#wsConnection$ = this.#api.createWebSocketConnection(
            accessToken,
            containerId,
            host,
            // Close observer to detect graceful vs abnormal close
            (event) => {
              // Code 1000 = normal closure (e.g., user typed 'exit')
              // Code 1001 = going away (e.g., server shutdown cleanly)
              wasGracefulClose = event.code === 1000 || event.code === 1001;
            }
          );
          return this.#wsConnection$;
        }).pipe(
          tap({
            next: () => {
              if (!hasReceivedMessage) {
                hasReceivedMessage = true;
                this.#setConnectionState({ status: 'connected' });
              }
            },
            complete: () => {
              // Only set disconnected if we were connected
              if (this.connectionState().status === 'connected') {
                this.#setConnectionState({ status: 'disconnected' });
              }
            },
            error: () => {
              // On error, check if it was actually a graceful close
              // WebSocket can emit error for clean closes depending on timing
              if (wasGracefulClose && this.connectionState().status === 'connected') {
                this.#setConnectionState({ status: 'disconnected' });
              }
            }
          }),
          // Don't retry if it was a graceful close
          catchError((err) => {
            if (wasGracefulClose) {
              return EMPTY; // Complete without retrying
            }
            throw err; // Re-throw to trigger retry
          })
        )
      ),
      catchError((err) => {
        throw err;
      })
    );
  }

  // Errors here come from the `auth$` POST (HttpErrorResponse) or from the websocket
  // (a CloseEvent, which carries `code`, never `status`), so the check is shape-driven
  // and never assumes a `status` exists.
  #isAuthorizationError(err: unknown): boolean {
    if (err instanceof HttpErrorResponse) {
      return err.status === 401 || err.status === 403;
    }

    // Fallback for HTTP errors that lost their prototype through an interceptor.
    const status = (err as { status?: unknown })?.status;
    return typeof status === 'number' && (status === 401 || status === 403);
  }

  #createConnectionStream() {
    const inputChanges$ = this.#getInputChanges$();
    const containerBecomesActive$ = this.#getContainerBecomesActive$();
    const containerBecomesInactive$ = this.#getContainerBecomesInactive$();

    // Combine inputs with container active state - this is the "ready to connect" signal
    const readyToConnect$ = combineLatest({
      inputs: inputChanges$,
      isActive: this.#containerActive$
    }).pipe(
      filter(({ isActive }) => isActive),
      map(({ inputs }) => inputs),
      distinctUntilChanged((a, b) => a.serviceId === b.serviceId && a.containerId === b.containerId)
    );

    // Manual reconnect gated on container being active
    const gatedManualReconnect$ = this.#manualReconnectTrigger$.pipe(
      withLatestFrom(this.#containerActive$, inputChanges$),
      filter(([ _, isActive ]) => isActive),
      map(([ _, __, inputs ]) => inputs)
    );

    // Container becoming active again (after being inactive)
    const containerReactivated$ = containerBecomesActive$.pipe(
      withLatestFrom(inputChanges$),
      map(([ _, inputs ]) => inputs)
    );

    // Connection trigger stream - never completes
    const connectionTrigger$ = merge(
      readyToConnect$,
      gatedManualReconnect$,
      containerReactivated$
    );

    return connectionTrigger$.pipe(
      tap(() => {
        // Close existing connection if any
        // isReconnect = true if we had a previous connection attempt (successful or failed)
        const status = this.connectionState().status;
        const isReconnect = status === 'connected' ||
          status === 'disconnected' ||
          status === 'container_inactive' ||
          status === 'failed' ||
          status === 'unauthorized';
        if (this.#wsConnection$) {
          this.#wsConnection$.complete();
        }
        this.#setConnectionState({ status: 'connecting', isReconnect });
      }),
      switchMap(({ serviceId, containerId }) =>
        this.#createWebSocketConnection$(serviceId, containerId).pipe(
          takeUntil(
            containerBecomesInactive$.pipe(
              tap(() => this.#setConnectionState({ status: 'container_inactive' }))
            )
          ),
          retry({
            count: MAX_RETRY_COUNT,
            delay: (err, retryCount) => {
              // Denial is deterministic, so stop instead of looping. EMPTY completes the
              // notifier without an error, keeping the state below and the outer
              // switchMap alive.
              if (this.#isAuthorizationError(err)) {
                this.#setConnectionState({ status: 'unauthorized' });
                return EMPTY;
              }

              return this.#containerActive$.pipe(
                take(1),
                switchMap((isActive) => {
                  if (!isActive) {
                    this.#setConnectionState({ status: 'container_inactive' });
                    return EMPTY;
                  }
                  this.#setConnectionState({ status: 'retrying', attempt: retryCount });
                  return timer(5000 * Math.min(retryCount, 10));
                })
              );
            }
          }),
          catchError(() => {
            this.#setConnectionState({ status: 'failed' });
            return EMPTY; // Inner stream completes, but outer switchMap stays alive
          })
        )
      ),
      takeUntilDestroyed(this.#destroyRef)
    );
  }

  #init() {
    const ngTerminal = this.terminalRef();

    ngTerminal.setXtermOptions({
      fontFamily: '"DejaVu Sans Mono", monospace',
      cursorBlink: true,
      fontSize: 13,
      convertEol: true
    });

    // The theme deliberately bypasses `setXtermOptions`. When ng-terminal sees a
    // `theme.background` it mirrors it onto its resize handles by walking
    // `document.styleSheets`, which throws a SecurityError on the cross-origin
    // Google Fonts sheets. That throw happens inside the `renderObservable`
    // subscriber, so it tears the subscription down on the very first render and
    // the terminal never fits itself again (last rows spill out of the box).
    // xterm's options setter is a partial merge, so this survives the
    // `xterm.options = { ...xtermOptions }` write ng-terminal does on every
    // render. The handles are coloured in SCSS instead.
    this.terminalInstance.options = {
      theme: {
        background: '#0b0c14'
      }
    };

    this.terminalInstance.loadAddon(new ClipboardAddon());
    this.terminalInstance.loadAddon(new WebLinksAddon());

    this.terminalInstance.onResize(({ rows, cols }) => {
      this.send(JSON.stringify({ rows, cols }), WebsocketMessageTypes.Resize);
    });

    // Handle user input
    ngTerminal
      .onData()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((input) => {
        this.send(input, WebsocketMessageTypes.Io);
      });

    // Main connection stream
    this.#createConnectionStream()
      .subscribe((msg) => {
        if (msg?.type === WebsocketMessageTypes.Io) {
          ngTerminal.write(msg.message || '');
          if (msg.message) {
            this.terminalOutput.emit(msg.message);
          }
        }

        if (msg?.type === WebsocketMessageTypes.Pong) {
          this.send('', WebsocketMessageTypes.Pong);
        }
      });
  }
}
